// HubSpot embed (v2) initializer for the contact page form.
// Reads portal/form/region IDs from the target div's data-* attributes —
// which in turn are populated from theme settings (see
// config/settings_schema.json → "HubSpot integration"). This keeps IDs as
// a single source of truth, avoiding hardcoded duplication.
//
// Also patches HubSpot's rendered markup for a11y: HubSpot's embed does not
// output `autocomplete`, `aria-required`, or `role="alert"`. We patch once
// when the form is ready and again on every re-render (HubSpot re-renders
// error markup on validation failure).

const AUTOCOMPLETE_MAP = {
  firstname: 'given-name',
  lastname: 'family-name',
  fullname: 'name',
  name: 'name',
  email: 'email',
  phone: 'tel',
  mobilephone: 'tel',
  company: 'organization',
  jobtitle: 'organization-title',
  address: 'street-address',
  city: 'address-level2',
  state: 'address-level1',
  zip: 'postal-code',
  country: 'country-name',
};

// Strip HubSpot's broken ARIA on radio groups. HubSpot renders:
//   <ul role="checkbox">                 ← wrong role
//     <li role="radio">                  ← nested with native <input type="radio">
//       <input type="radio">
//
// This violates WCAG 4.1.2 (nested interactive controls + role mismatch).
// Native <input type="radio"> is fully accessible — no ARIA needed.
// We strip the bad roles so the native HTML semantics work cleanly.
const patchHubSpotRadioGroups = (formEl) => {
  formEl.querySelectorAll('ul.inputs-list[role="checkbox"]').forEach((ul) => {
    ul.removeAttribute('role');
  });

  formEl.querySelectorAll('li.hs-form-radio[role="radio"]').forEach((li) => {
    li.removeAttribute('role');
    li.removeAttribute('aria-checked');
  });
};

const patchHubSpotForm = (formEl) => {
  if (!formEl) return;

  formEl.querySelectorAll('input, textarea, select').forEach((field) => {
    const name = (field.name || '').toLowerCase().replace(/.*_/, '');
    const ac = AUTOCOMPLETE_MAP[name];

    if (ac && !field.hasAttribute('autocomplete')) {
      field.setAttribute('autocomplete', ac);
    }
    if (field.required && !field.hasAttribute('aria-required')) {
      field.setAttribute('aria-required', 'true');
    }
  });

  formEl.querySelectorAll('.hs-error-msgs, .hs-error-msg').forEach((errEl) => {
    if (!errEl.hasAttribute('role')) errEl.setAttribute('role', 'alert');
    if (!errEl.hasAttribute('aria-live')) errEl.setAttribute('aria-live', 'polite');
  });

  patchHubSpotRadioGroups(formEl);
};

const targetEl = /** @type {HTMLElement | null} */ (
  document.querySelector('#hubspot-contact-form-contact')
);

const initContactForm = () => {
  if (!targetEl) return;
  /** @type {any} */
  const w = window;
  if (!w.hbspt || typeof w.hbspt.forms?.create !== 'function') return;

  w.hbspt.forms.create({
    portalId: targetEl.dataset.portalId,
    formId: targetEl.dataset.formId,
    region: targetEl.dataset.region || 'na1',
    target: '#' + targetEl.id,
    onFormReady: ($form) => {
      const formEl = ($form && $form[0]) || targetEl.querySelector('form');
      patchHubSpotForm(formEl);

      if (formEl) {
        const observer = new MutationObserver(() => patchHubSpotForm(formEl));
        observer.observe(formEl, { childList: true, subtree: true });
      }
    },
  });
};

// HubSpot's embed loads asynchronously — poll briefly until ready, then init.
// Bails after ~5 s to avoid wasted work on slow networks.
if (targetEl) {
  /** @type {any} */
  const w = window;
  if (w.hbspt && typeof w.hbspt.forms?.create === 'function') {
    initContactForm();
  } else {
    let attempts = 0;
    const timer = setInterval(() => {
      attempts += 1;
      /** @type {any} */
      const ww = window;
      if (ww.hbspt && typeof ww.hbspt.forms?.create === 'function') {
        clearInterval(timer);
        initContactForm();
      } else if (attempts > 50) {
        clearInterval(timer);
      }
    }, 100);
  }
}
